import {useDebouncedValue, useToggle} from '@mantine/hooks';
import {useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {Button, Chip, Input, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger} from "@heroui/react";
import AudioPlayButton from '../AudioPlayButton.jsx';
import {appHelper} from '../../helpers/AppHelper.jsx';
import {twMerge} from 'tailwind-merge';
import {useRecoilState} from 'recoil';
import {userState} from '../../store.jsx';

const inputWrapper = 'min-h-12s !bg-white border-1 data-[focus=true]:!border-default data-[hover=true]:border-default shadow-none';

export default function Voices({
																 data = [],
																 onChange,
																 value,
																 valueKey = 'voice_id',
																 isShowCndContent = true,
																 textValueKey,
																 voiceKey,
																 isDisabled=false,
																 ...props
															 }) {
	const [open, setOpen] = useToggle()
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebouncedValue(search, 300)
	const [authMe] = useRecoilState(userState)
	const [getCurrentVoice, setCurrentVoice] = useState(null)

	useEffect(() => {
		setSearch('')
		setCurrentVoice(_.find(data, (item) => _.get(item, valueKey) === value))
	}, [value, data]);

	const allVoices = useMemo(() => {
		const voices = appHelper.filterArrayContent(
			data,
			debouncedSearch,
			(item) => `
						${item?.voice || ''}
            ${item?.speaker_name || ''}
            ${item?.friendly_name || ''}
            ${item?.speaker_type || ''}
            ${item?.gender || ''}
            ${item?.provider || ''}
        `
		);

		return _.orderBy(
			voices,
			['is_bookmarked', 'is_custom', 'speaker_type', 'language'],
			['desc', 'desc', 'desc', 'desc']
		);
	}, [debouncedSearch, data]);


	const enabledOrDisabled = (item) => {
		const isPremium = _.lowerCase(item?.speaker_type || 'standard') === 'premium';
		const isPlanEligible = (authMe?.current_plan || 1) >= 2;
		return isPremium && !isPlanEligible;
	};

	return (
		<>
			<Popover
				isOpen={open}
				onOpenChange={setOpen}
				shouldFlip={true}
				offset={10}
				classNames={{
					base: 'w-full max-w-96 w-screen',
				}}
				placement="bottom">
				<PopoverTrigger>
					<Button
						isDisabled={isDisabled}
						fullWidth variant="bordered"
						endContent={<i className="hgi-stroke hgi-arrow-right-01"></i>}
						className={twMerge([
							"border border-slate-800/10 min-h-12 max-h-16 h-14 px-3 !scale-100 justify-between bg-white",
							props?.classNames?.trigger
						])}>
						{!_.isEmpty(getCurrentVoice) ?
							<div className="flex gap-3 items-center justify-start py-2">
								<AudioPlayButton
									className="bg-default-300 border border-slate-50 text-black cursor-pointer"
									src={_.get(getCurrentVoice, (voiceKey || 'audio_file'))}/>
								<span className="truncate">
         {_.startCase(_.get(getCurrentVoice, (textValueKey || 'voice')))}
        </span>
							</div>
							:
							<>
								Select voice...
							</>
						}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="p-2 w-full">
					<div className="w-full">
						<div className="mb-2 border-b border-slate-200/30 pb-2">
							<Input
								label=""
								placeholder="Search here..."
								labelPlacement="outside"
								type="text"
								value={search}
								onValueChange={setSearch}
								classNames={{
									inputWrapper,
									label: 'text-slate-500 mb-10 top-5',
								}}
								endContent={search && <Button
									isIconOnly size="sm" radius="full"
									variant="light" color="danger"
									onPress={() => setSearch('')}
									children={<i className="hgi-stroke hgi-cancel-01"></i>}
								/>}
							/>
						</div>
						<Listbox
							aria-label="Actions"
							aria-labelledby="Actions"
							classNames={{
								list: 'p-0',
								emptyContent: 'text-center',
							}}
							selectionMode="single"
							disallowEmptySelection
							selectedKeys={[value || '']}
							onSelectionChange={(keys) => {
								onChange(valueKey, keys.currentKey)
								setOpen(false)
							}}
							items={allVoices || []}
							isVirtualized
							virtualization={{
								maxListboxHeight: 300,
								itemHeight: 60
							}}
						>
							{(item) => (
								<ListboxItem
									aria-label="Actions"
									aria-labelledby="Actions"
									isDisabled={enabledOrDisabled(item)}
									title={_.startCase(_.get(item, (textValueKey || 'voice')))}
									key={_.get(item, valueKey)}
									description={!_.isEmpty(item?.language) &&
										<div className="flex gap-1 mt-1 mb-1 items-center">
											{_.isArray(item?.language) ?
												item?.language?.map((language, index) => (
													<Chip size="sm" key={language}
																className="bg-slate-200">{language}</Chip>
												))
												:
												<Chip size="sm" className="bg-slate-200">{item?.language}</Chip>
											}

											{item?.is_cloned && (
													<Chip size="sm" key={'cloned'}
																className="bg-green-500 text-white">Clone</Chip>
												)}

										</div>}
									endContent={<div className="flex items-center justify-end gap-1">
										{item?.is_bookmarked &&
											<div
												className="bg-primary shadow-medium text-white w-6 h-6 rounded-full flex items-center justify-center">
												<i className="hgi-stroke hgi-star"></i>
											</div>
										}
										{isShowCndContent &&
											<Chip
												size="sm"
												color={_.lowerCase(item?.speaker_type || 'standard') === 'premium' ? 'warning' : 'default'}
												children={item?.speaker_type || 'Standard'}
											/>
										}
									</div>}
									startContent={<AudioPlayButton
										className="bg-default-300 border border-slate-50 text-black cursor-pointer"
										src={_.get(item, (voiceKey || 'audio_file'))}/>}
									classNames={{
										base: 'data-[hover=true]:bg-default-50 data-[selected=true]:!bg-default-100 data-[focus=true]:!bg-default-100'
									}}
								>
								</ListboxItem>
							)}
						</Listbox>
					</div>
				</PopoverContent>
			</Popover>
		</>
	);
}